// src/components/Dashboard/widgets/SpendBudgetWidget.tsx
// Widget 3 — Spend vs Budget (chart)
// Click any job bar → Spend Analytics filtered to that job.
// Click over-budget badge → Spend Analytics over-budget view.
import { BarChart3, ChevronRight, AlertTriangle } from 'lucide-react'
import { MOCK_JOBS, MOCK_POS } from '../../../utils/mockData'
import { formatCurrency } from '../../../utils/formatters'
import { WidgetProps } from '../types'
import { useDeepLinkNavigation } from '../../../hooks/useDeepLinkNavigation'

export function SpendBudgetWidget({ onNavigate, scopedJobIds }: WidgetProps) {
  const { navigate } = useDeepLinkNavigation('dashboard', onNavigate)

  const activeJobs = MOCK_JOBS.filter(j => {
    if (!['ACTIVE', 'ON_HOLD'].includes(j.status)) return false
    return scopedJobIds ? scopedJobIds.includes(j.id) : true
  })

  const jobData = activeJobs.map(job => {
    const pos       = (MOCK_POS as any[]).filter(p => p.jobId === job.id && p.status !== 'REJECTED')
    const committed = pos.reduce((s: number, p: any) => s + (p.totalExGST ?? 0), 0)
    const pct       = job.contractValue > 0 ? Math.round(committed / job.contractValue * 100) : 0
    return { job, committed, pct, over: pct > 90 }
  }).slice(0, 5)

  const totalBudget    = activeJobs.reduce((s, j) => s + j.contractValue, 0)
  const totalCommitted = jobData.reduce((s, d) => s + d.committed, 0)
  const overCount      = jobData.filter(d => d.over).length
  const portfolioPct   = totalBudget > 0 ? Math.round(totalCommitted / totalBudget * 100) : 0

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
      {/* Header */}
      <div
        className="px-5 py-3.5 border-b flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => navigate('spend-analytics')}
      >
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-purple-600" />
          <span className="font-semibold text-gray-900 text-sm">Spend vs budget</span>
          {overCount > 0 && (
            <span
              className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5 cursor-pointer"
              onClick={e => { e.stopPropagation(); navigate('spend-analytics', { openTab: 'over-budget' }) }}
            >
              <AlertTriangle className="w-2.5 h-2.5" />{overCount} over
            </span>
          )}
        </div>
        <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
      </div>

      {/* Portfolio total */}
      <div className="px-5 py-3 border-b bg-gray-50/60">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-gray-500">Portfolio committed</span>
          <span className={`text-xs font-bold ${portfolioPct > 90 ? 'text-red-600' : portfolioPct > 70 ? 'text-amber-600' : 'text-gray-700'}`}>
            {portfolioPct}%
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${portfolioPct > 90 ? 'bg-red-500' : portfolioPct > 70 ? 'bg-amber-500' : 'bg-purple-500'}`}
            style={{ width: `${Math.min(portfolioPct, 100)}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>{formatCurrency(totalCommitted)}</span>
          <span>{formatCurrency(totalBudget)}</span>
        </div>
      </div>

      {/* Per-job bars */}
      <div className="divide-y divide-gray-50 flex-1">
        {jobData.map(({ job, committed, pct, over }) => (
          <div
            key={job.id}
            className={`px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors group ${over ? 'bg-red-50/30' : ''}`}
            onClick={() => navigate('spend-analytics', { openId: job.id })}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-gray-900">{job.jobNumber}</span>
                {over && <AlertTriangle className="w-3 h-3 text-red-500" />}
              </div>
              <span className={`text-xs font-bold ${over ? 'text-red-600' : pct > 70 ? 'text-amber-600' : 'text-gray-500'}`}>
                {pct}%
              </span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${over ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-purple-500'}`}
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>{formatCurrency(committed)}</span>
              <span>of {formatCurrency(job.contractValue)}</span>
            </div>
          </div>
        ))}

        {jobData.length === 0 && (
          <div className="px-5 py-8 text-center">
            <BarChart3 className="w-6 h-6 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No active jobs</p>
          </div>
        )}
      </div>
    </div>
  )
}
